import React, { useState, useEffect, useMemo } from 'react';
import LoadingPlaceholder from '../ui/Loading/LoadingPlaceholder';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { MoreVertical, Pencil, Trash2, Plus } from 'lucide-react';
import SpaPromotionModal from './SpaPromotionModal';
import {
  fetchSpaPromotions,
  createSpaPromotion,
  updateSpaPromotion,
  deleteSpaPromotion,
  fetchSingleSpa
} from '../../api/spaApi';

// ConfirmDialog component
function ConfirmDialog({ open, title, description, onCancel, onConfirm, loading }) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{description}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button onClick={onConfirm} color="error" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PROMO_STATUS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Expired', value: 'expired' },
  { label: 'Inactive', value: 'inactive' },
];
  // Helper to compute status for filtering and rendering
  const computeStatus = (promo) => {
    // default to stored status
    let computed = promo.status || 'inactive';
    if (promo.status !== 'inactive') {
      const start = promo.startDate ? new Date(promo.startDate) : null;
      const end = promo.endDate ? new Date(promo.endDate) : null;
      const now = new Date();
      if (start && end) {
        if (now < start) computed = 'upcoming';
        else if (now >= start && now <= end) computed = 'active';
        else if (now > end) computed = 'expired';
      }
    }
    return computed;
  };

// Demo/mock data for initial state
const mockSpaPromotions = [
  {
    id: 1,
    title: 'August Special',
    discount: '20%',
    status: 'active',
    startDate: '2025-08-01',
    endDate: '2025-08-31',
  },
  {
    id: 2,
    title: 'Facial Combo',
    discount: '15%',
    status: 'inactive',
    startDate: '2025-07-01',
    endDate: '2025-07-15',
  },
];

export default function SpaPromotion({ spaId, propertyId }) {
  const [tab, setTab] = useState('all');
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [spaItems, setSpaItems] = useState([]);
  const [selectedSpaItemId, setSelectedSpaItemId] = useState([]);

  // Placeholder: Simulate fetching spa promotions
  useEffect(() => {
    if (!spaId) return;
    setLoading(true);
    fetchSpaPromotions(spaId, propertyId)
      .then(data => setPromotions(data.promotions || []))
      .catch(err => console.error('Failed to fetch spa promotions:', err))
      .finally(() => setLoading(false));
  }, [spaId, propertyId]);

  // Fetch spa items (massages) for table rendering (ensure available for item names)
  useEffect(() => {
    if (!spaId || !propertyId) return;
    fetchSingleSpa(spaId, propertyId)
      .then(data => setSpaItems(data.massages || []))
      .catch(err => {
        console.error('Failed to fetch spa items:', err);
        setSpaItems([]);
      });
  }, [spaId, propertyId]);

  // Fetch spa items (massages) when modal opens
  useEffect(() => {
    if (formOpen && spaId) {
      fetchSingleSpa(spaId, propertyId)
        .then(data => {
          setSpaItems(data.massages || []);
          if (editData && editData.massageItems) {
            setSelectedSpaItemId(editData.massageItems);
          }
        })
        .catch(err => {
          console.error('Failed to fetch spa items:', err);
          setSpaItems([]);
        });
    }
  }, [formOpen, spaId, editData, propertyId]);

  // Debug: log editData being passed
  useEffect(() => {
    console.log("🐛 editData being passed:", editData);
  }, [editData]);

  const filteredPromotions = useMemo(() => {
    if (tab === 'all') return promotions;
    return promotions.filter((p) => computeStatus(p) === tab);
  }, [promotions, tab]);

  // Tab counters for each filter
  const tabCounts = useMemo(() => {
    const counts = { all: promotions.length, active: 0, upcoming: 0, expired: 0, inactive: 0 };
    promotions.forEach((p) => {
      const status = computeStatus(p);
      if (status === 'active') counts.active += 1;
      else if (status === 'upcoming') counts.upcoming += 1;
      else if (status === 'expired') counts.expired += 1;
      else counts.inactive += 1;
    });
    return counts;
  }, [promotions]);

  // Action menu handlers
  const handleActionMenuOpen = (event, promo) => {
    setActionMenu(event.currentTarget);
    setSelectedPromotion(promo);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
    setSelectedPromotion(null);
  };

  // Create/Edit Promotion
  const handleFormOpen = () => {
    setEditData(null);
    setSelectedSpaItemId([]); // Only reset here for new creation
    setFormOpen(true);
  };
  const handleEditPromotion = (promo) => {
    setEditData(promo);
    setFormOpen(true);
    handleActionMenuClose();
  };
  const handleFormClose = () => {
    setFormOpen(false);
    setEditData(null);
    setSpaItems([]);
    setSelectedSpaItemId([]);
  };
  const handleFormSubmit = async (form) => {
    setFormLoading(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        discount: parseFloat(form.discount),
        status: form.status,
        startDate: form.startDate,
        endDate: form.endDate,
        massageItems: selectedSpaItemId // link with selected massage item ids
      };

      if (editData && editData._id) {
        await updateSpaPromotion(editData._id, payload, spaId, propertyId);
      } else {
        await createSpaPromotion(payload, spaId, propertyId);
      }

      const data = await fetchSpaPromotions(spaId, propertyId);
      setPromotions(data.promotions || []);
      // Refetch updated massage items after edit to update names
      const updatedSpa = await fetchSingleSpa(spaId, propertyId);
      setSpaItems(updatedSpa.massages || []);
      setFormOpen(false);
      setEditData(null);
      // setSpaItems([]); // Removed to fix "Unnamed" items bug
      setSelectedSpaItemId([]);
    } catch (err) {
      console.error('❌ Failed to save promotion:', err);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete Promotion
  const handleDeletePromotion = (promo) => {
    setSelectedPromotion(promo);
    setDeleteDialogOpen(true);
    handleActionMenuClose();
  };
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedPromotion(null);
  };
  const handleDeleteDialogConfirm = async () => {
    setDeleteLoading(true);
    try {
      if (selectedPromotion?._id) {
        await deleteSpaPromotion(selectedPromotion._id, propertyId, spaId);
        const data = await fetchSpaPromotions(spaId, propertyId);
        setPromotions(data.promotions || []);
      }
    } catch (err) {
      console.error('❌ Failed to delete promotion:', err);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setSelectedPromotion(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ flexGrow: 1 }}
        >
          {PROMO_STATUS.map((s) => (
            <Tab
              key={s.value}
              label={`${s.label} (${tabCounts[s.value] ?? 0})`}
              value={s.value}
            />
          ))}
        </Tabs>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={handleFormOpen}
          sx={{ ml: 2 }}
        >
          New Promotion
        </Button>
      </Box>
      {loading ? (
        <LoadingPlaceholder />
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Promotion Title</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPromotions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No promotions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPromotions.map((promo) => {
                  // Debug: Log the raw promotion data
                  console.log('🧪 Promotion data:', promo);
                  // Compute status (active/upcoming/expired/inactive)
                  const computedStatus = computeStatus(promo);
                    // Massage item tooltips
                    const itemNames = promo.massageItems
                      ? promo.massageItems.map(
                          (id) => spaItems.find((m) => m._id === id)?.typeOfMassage || 'Unnamed'
                        )
                      : [];
                    const preview = itemNames.slice(0, 5).join(', ');
                    const remainingCount = itemNames.length - 5;
                    const fullText = itemNames.join(', ');
                    // Discount formatting
                    let discountDisplay = '';
                    if (typeof promo.discount === 'number') {
                      discountDisplay = `${promo.discount}%`;
                    } else if (typeof promo.discount === 'string' && !promo.discount.endsWith('%')) {
                      discountDisplay = `${promo.discount}%`;
                    } else {
                      discountDisplay = promo.discount;
                    }
                    // Date formatting
                    const startDateStr = promo.startDate
                      ? new Date(promo.startDate).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : 'N/A';

                    const endDateStr = promo.endDate
                      ? new Date(promo.endDate).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : 'N/A';
                    // Status badge color
                    let chipColor = 'default';
                    if (computedStatus === 'active') chipColor = 'success';
                    else if (computedStatus === 'upcoming') chipColor = 'info';
                    else if (computedStatus === 'expired') chipColor = 'warning';
                    else if (computedStatus === 'inactive') chipColor = 'default';
                    return (
                      <TableRow key={promo._id}>
                        <TableCell>{promo.title}</TableCell>
                        <TableCell>
                          <Tooltip title={fullText} arrow>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 180 }}>
                              {preview}
                              {remainingCount > 0 && (
                                <span style={{ color: '#888' }}>
                                  {`, +${remainingCount} more`}
                                </span>
                              )}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{discountDisplay}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              computedStatus.charAt(0).toUpperCase() + computedStatus.slice(1)
                            }
                            color={chipColor}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{startDateStr}</TableCell>
                        <TableCell>{endDateStr}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, promo)}
                          >
                            <MoreVertical size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {/* Action Menu */}
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => handleEditPromotion(selectedPromotion)}
          disabled={!selectedPromotion}
        >
          <Pencil size={16} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDeletePromotion(selectedPromotion)}
          disabled={!selectedPromotion}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
      {/* Promotion Form Dialog */}
      <SpaPromotionModal
        open={formOpen}
        onClose={handleFormClose}
        editData={editData}
        form={
          editData
            ? {
                title: editData.title || '',
                description: editData.description || '',
                discount: editData.discount?.toString() || '',
                startDate: editData.startDate?.substring(0, 10) || '',
                endDate: editData.endDate?.substring(0, 10) || '',
                status: editData.status || 'active',
              }
            : {
                title: '',
                description: '',
                discount: '',
                startDate: '',
                endDate: '',
                status: 'active',
              }
        }
        setForm={(updated) => {
          console.log("🐛 Promotion form update triggered with:", updated);
          setEditData((prev) => ({ ...prev, ...updated }));
        }}
        spaItems={spaItems}
        selectedSpaItemId={selectedSpaItemId}
        setSelectedSpaItemId={setSelectedSpaItemId}
        onSubmit={(payload) =>
          handleFormSubmit({
            ...payload,
            spaId,
            propertyId
          })
        }
        loading={formLoading}
      />
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Promotion"
        description={
          selectedPromotion
            ? `Are you sure you want to delete the promotion "${selectedPromotion.title}"? This action cannot be undone.`
            : ''
        }
        onCancel={handleDeleteDialogClose}
        onConfirm={handleDeleteDialogConfirm}
        loading={deleteLoading}
      />
    </Box>
  );
}